import { useEffect, useMemo, useState } from "react";
import { X, Plus, Trash, ArrowsClockwise, MagnifyingGlass, UserPlus, PaperPlaneTilt } from "@phosphor-icons/react";
import { api, formatUSD, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const INTERVALS = [
  { value: "monthly",   label: "Monthly" },
  { value: "weekly",    label: "Weekly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "yearly",    label: "Yearly" },
];

const blankLine = () => ({
  description: "",
  quantity: 1,
  unit_price: "",
  recurring: false,
  recurring_interval: "monthly",
});

/** Create a custom invoice — for an existing client OR a brand-new one. */
export function CreateInvoiceModal({ open, onClose, onCreated, invoice }) {
  const isEdit = !!invoice;
  const [clients, setClients] = useState([]);
  const [clientMode, setClientMode] = useState("existing"); // "existing" | "new"
  const [search, setSearch] = useState("");
  const [selectedClientId, setSelectedClientId] = useState("");
  const [newClient, setNewClient] = useState({ name: "", email: "", company: "" });

  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [dueDate, setDueDate] = useState(plus14());
  const [taxPct, setTaxPct] = useState("0");
  const [lines, setLines] = useState([blankLine()]);
  const [sendImmediately, setSendImmediately] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setTitle(invoice.title || "");
      setNotes(invoice.notes || "");
      setDueDate((invoice.due_date || "").slice(0, 10) || plus14());
      setTaxPct(String(invoice.tax_pct ?? 0));
      setLines((invoice.line_items || []).map((l) => ({
        id: l.id,
        description: l.description || "",
        quantity: l.quantity ?? 1,
        unit_price: l.unit_price ?? "",
        recurring: !!l.recurring,
        recurring_interval: l.recurring_interval || "monthly",
      })));
      // Client is locked in edit mode
      setClientMode("existing");
      setSelectedClientId(invoice.client_id);
      setSendImmediately(false);
    } else {
      setTitle(""); setNotes(""); setDueDate(plus14()); setTaxPct("0");
      setLines([blankLine()]);
      setClientMode("existing"); setSearch(""); setSelectedClientId("");
      setNewClient({ name: "", email: "", company: "" });
      setSendImmediately(true);
    }
    (async () => {
      try {
        const { data } = await api.get("/admin/clients");
        setClients(data || []);
      } catch (e) {
        // soft fail — admin can still create via "new"
      }
    })();
  }, [open, isEdit, invoice]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clients.slice(0, 50);
    return clients.filter((c) =>
      (c.name || "").toLowerCase().includes(q) ||
      (c.email || "").toLowerCase().includes(q) ||
      (c.company_name || "").toLowerCase().includes(q)
    ).slice(0, 50);
  }, [clients, search]);

  const subtotal = useMemo(
    () => lines.reduce((s, l) => s + (Number(l.quantity || 0) * Number(l.unit_price || 0)), 0),
    [lines]
  );
  const tax = round2(subtotal * (Number(taxPct || 0) / 100));
  const total = round2(subtotal + tax);
  const hasRecurring = lines.some((l) => l.recurring);

  if (!open) return null;

  const updateLine = (idx, patch) =>
    setLines((arr) => arr.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  const removeLine = (idx) => setLines((arr) => arr.length === 1 ? arr : arr.filter((_, i) => i !== idx));
  const addLine = () => setLines((arr) => [...arr, blankLine()]);

  const validate = () => {
    if (!title.trim()) return "Title is required";
    if (clientMode === "existing" && !selectedClientId) return "Pick a client";
    if (clientMode === "new") {
      if (!newClient.name.trim()) return "Client name is required";
      if (!newClient.email.trim() || !newClient.email.includes("@")) return "Valid client email is required";
    }
    for (const [i, l] of lines.entries()) {
      if (!l.description.trim()) return `Line ${i + 1}: description required`;
      if (!(Number(l.quantity) > 0)) return `Line ${i + 1}: quantity must be > 0`;
      if (!(Number(l.unit_price) >= 0)) return `Line ${i + 1}: unit price must be ≥ 0`;
    }
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v !== true) return toast.error(v);
    setSaving(true);
    try {
      if (isEdit) {
        const patchBody = {
          title: title.trim(),
          notes: notes.trim() || null,
          due_date: dueDate || null,
          tax_pct: Number(taxPct || 0),
          line_items: lines.map((l) => ({
            id: l.id,
            description: l.description.trim(),
            quantity: Number(l.quantity),
            unit_price: Number(l.unit_price),
            recurring: !!l.recurring,
            recurring_interval: l.recurring ? l.recurring_interval : null,
          })),
        };
        const { data } = await api.patch(`/admin/invoices/${invoice.id}`, patchBody);
        toast.success(`Invoice ${data.invoice_number} updated`);
        onCreated?.(data);
        return;
      }
      const client = clientMode === "existing"
        ? { existing_client_id: selectedClientId }
        : {
            name: newClient.name.trim(),
            email: newClient.email.trim().toLowerCase(),
            company: newClient.company.trim() || null,
          };
      const payload = {
        client,
        title: title.trim(),
        notes: notes.trim() || null,
        due_date: dueDate || null,
        tax_pct: Number(taxPct || 0),
        send_immediately: sendImmediately,
        line_items: lines.map((l) => ({
          description: l.description.trim(),
          quantity: Number(l.quantity),
          unit_price: Number(l.unit_price),
          recurring: !!l.recurring,
          recurring_interval: l.recurring ? l.recurring_interval : null,
        })),
      };
      const { data } = await api.post("/admin/invoices", payload);
      toast.success(sendImmediately
        ? `Invoice ${data.invoice_number} sent`
        : `Draft ${data.invoice_number} created`);
      onCreated?.(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || (isEdit ? "Could not update invoice" : "Could not create invoice"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/80 p-4 overflow-y-auto" data-testid={isEdit ? "edit-invoice-modal" : "create-invoice-modal"}>
      <div className="bg-[#0a0a0a] border border-[#FF2D2D] w-full max-w-3xl my-8">
        <div className="sticky top-0 z-10 flex items-center justify-between p-5 border-b border-[#262626] bg-[#0a0a0a]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">{isEdit ? `Edit invoice · ${invoice.invoice_number}` : "New invoice"}</div>
            <div className="font-display text-2xl mt-1">{isEdit ? `Update ${(invoice.status || "").toLowerCase() === "draft" ? "this draft" : "this invoice"}` : "Bill a client"}</div>
          </div>
          <button onClick={onClose} className="p-1.5 text-[#a3a3a3] hover:text-[#FF2D2D]" aria-label="Close" data-testid="invoice-modal-close">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-6">
          {/* Client picker — hidden in edit mode (client cannot be changed once draft is created) */}
          {!isEdit && (
          <section>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#a3a3a3] mb-2">Client</div>
            <div className="grid grid-cols-2 gap-px bg-[#262626] border border-[#262626] mb-3" role="radiogroup">
              <button type="button" onClick={() => setClientMode("existing")} data-testid="client-mode-existing"
                className={`px-3 py-2 text-xs uppercase tracking-wider transition-colors ${clientMode === "existing" ? "bg-[#FF2D2D] text-white" : "bg-[#0a0a0a] text-[#a3a3a3] hover:bg-[#121212]"}`}>
                <MagnifyingGlass size={11} className="inline mr-1.5" weight="bold" /> Pick existing
              </button>
              <button type="button" onClick={() => setClientMode("new")} data-testid="client-mode-new"
                className={`px-3 py-2 text-xs uppercase tracking-wider transition-colors ${clientMode === "new" ? "bg-[#FF2D2D] text-white" : "bg-[#0a0a0a] text-[#a3a3a3] hover:bg-[#121212]"}`}>
                <UserPlus size={11} className="inline mr-1.5" weight="bold" /> Create new
              </button>
            </div>

            {clientMode === "existing" ? (
              <div className="space-y-2">
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name, email, company…"
                  data-testid="client-search"
                  className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-sm text-white focus:border-[#FF2D2D] outline-none"
                />
                <div className="max-h-48 overflow-y-auto border border-[#262626] divide-y divide-[#262626]" data-testid="client-list">
                  {filtered.length === 0 ? (
                    <div className="p-3 text-xs text-[#a3a3a3]">No matches.</div>
                  ) : filtered.map((c) => (
                    <label key={c.id} className={`flex items-center gap-3 p-2.5 cursor-pointer hover:bg-[#121212] ${selectedClientId === c.id ? "bg-[#FF2D2D]/10 border-l-2 border-[#FF2D2D]" : ""}`}>
                      <input type="radio" name="client" value={c.id} checked={selectedClientId === c.id}
                        onChange={() => setSelectedClientId(c.id)} className="accent-[#FF2D2D]"
                        data-testid={`pick-client-${c.id}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white truncate">{c.name}</div>
                        <div className="text-[11px] text-[#a3a3a3] truncate">{c.email}{c.company_name ? ` · ${c.company_name}` : ""}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input label="Name *" value={newClient.name} onChange={(v) => setNewClient((c) => ({ ...c, name: v }))} testid="new-client-name" />
                <Input label="Email *" type="email" value={newClient.email} onChange={(v) => setNewClient((c) => ({ ...c, email: v }))} testid="new-client-email" />
                <Input label="Company" value={newClient.company} onChange={(v) => setNewClient((c) => ({ ...c, company: v }))} testid="new-client-company" />
              </div>
            )}
          </section>
          )}

          {isEdit && (
            <div className="border border-[#262626] bg-[#050505] px-4 py-3 text-xs text-[#a3a3a3]" data-testid="edit-client-locked">
              Billed to <span className="text-white font-semibold">{invoice.client_name}</span>
              {invoice.client_company ? ` · ${invoice.client_company}` : ""} · <span className="text-[#a3a3a3]">{invoice.client_email}</span>
              <span className="ml-2 text-[10px] uppercase tracking-wider text-[#666]">(cannot change client on an existing invoice)</span>
            </div>
          )}

          {/* Header fields */}
          <section className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <Input label="Invoice title *" value={title} onChange={setTitle} testid="invoice-title"
                placeholder="e.g. Brand refresh — November" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">Due date</label>
              <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="invoice-due-date"
                className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-white focus:border-[#FF2D2D] outline-none" />
            </div>
          </section>

          {/* Line items */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#a3a3a3]">Line items</div>
              <button type="button" onClick={addLine} data-testid="add-line-item"
                className="text-[10px] uppercase tracking-wider text-[#FF2D2D] hover:text-white flex items-center gap-1">
                <Plus size={11} weight="bold" /> Add line
              </button>
            </div>
            <div className="space-y-2">
              {lines.map((l, idx) => (
                <div key={idx} className="border border-[#262626] bg-[#050505] p-3 space-y-2" data-testid={`line-item-${idx}`}>
                  <div className="grid grid-cols-12 gap-2">
                    <input type="text" placeholder="Description" value={l.description}
                      onChange={(e) => updateLine(idx, { description: e.target.value })}
                      data-testid={`line-desc-${idx}`}
                      className="col-span-12 md:col-span-6 bg-[#0a0a0a] border border-[#262626] px-3 py-2 text-sm text-white focus:border-[#FF2D2D] outline-none" />
                    <input type="number" step="0.01" min="0.01" placeholder="Qty" value={l.quantity}
                      onChange={(e) => updateLine(idx, { quantity: e.target.value })}
                      data-testid={`line-qty-${idx}`}
                      className="col-span-4 md:col-span-2 bg-[#0a0a0a] border border-[#262626] px-3 py-2 text-sm text-white tabular-nums focus:border-[#FF2D2D] outline-none" />
                    <div className="col-span-6 md:col-span-3 relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#a3a3a3] text-sm">$</span>
                      <input type="number" step="0.01" min="0" placeholder="Unit price" value={l.unit_price}
                        onChange={(e) => updateLine(idx, { unit_price: e.target.value })}
                        data-testid={`line-unit-${idx}`}
                        className="w-full bg-[#0a0a0a] border border-[#262626] pl-7 pr-3 py-2 text-sm text-white tabular-nums focus:border-[#FF2D2D] outline-none" />
                    </div>
                    <button type="button" onClick={() => removeLine(idx)} title="Remove line"
                      disabled={lines.length === 1}
                      data-testid={`remove-line-${idx}`}
                      className="col-span-2 md:col-span-1 border border-[#262626] text-[#a3a3a3] hover:text-[#FF2D2D] hover:border-[#FF2D2D] disabled:opacity-30 disabled:cursor-not-allowed">
                      <Trash size={12} className="mx-auto" />
                    </button>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <label className="flex items-center gap-2 cursor-pointer text-xs text-[#a3a3a3]">
                      <input type="checkbox" checked={l.recurring}
                        onChange={(e) => updateLine(idx, { recurring: e.target.checked })}
                        className="accent-[#FF2D2D]" data-testid={`line-recurring-${idx}`} />
                      <ArrowsClockwise size={11} weight="bold" className={l.recurring ? "text-[#FF2D2D]" : ""} />
                      Recurring
                    </label>
                    {l.recurring && (
                      <select value={l.recurring_interval}
                        onChange={(e) => updateLine(idx, { recurring_interval: e.target.value })}
                        data-testid={`line-interval-${idx}`}
                        className="bg-[#0a0a0a] border border-[#262626] px-2 py-1 text-xs text-white focus:border-[#FF2D2D] outline-none">
                        {INTERVALS.map((iv) => <option key={iv.value} value={iv.value}>{iv.label}</option>)}
                      </select>
                    )}
                    <div className="ml-auto text-xs text-[#a3a3a3] tabular-nums">
                      Line total: <span className="text-white">{formatUSD(Number(l.quantity || 0) * Number(l.unit_price || 0))}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Notes + tax */}
          <section className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <label className="block text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">Notes (visible on invoice)</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                data-testid="invoice-notes"
                placeholder="Payment terms, references, thanks…"
                className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-sm text-white focus:border-[#FF2D2D] outline-none" />
            </div>
            <Input label="Tax %" type="number" min="0" max="100" step="0.01" value={taxPct} onChange={setTaxPct} testid="invoice-tax" />
          </section>

          {/* Totals */}
          <section className="border border-[#262626] bg-[#050505]">
            <Row label="Subtotal" value={formatUSD(subtotal)} />
            {Number(taxPct) > 0 && <Row label={`Tax (${Number(taxPct).toFixed(2)}%)`} value={formatUSD(tax)} />}
            <Row label="Total" value={formatUSD(total)} accent />
            {hasRecurring && (
              <div className="px-4 py-2 text-[11px] text-[#FF2D2D] border-t border-[#262626] flex items-center gap-1.5">
                <ArrowsClockwise size={11} weight="bold" /> Recurring lines will auto-draft a new invoice each interval.
              </div>
            )}
          </section>

          {/* Send-immediately toggle — only on create; edit keeps it a draft */}
          {!isEdit && (
          <label className="flex items-start gap-3 border border-[#262626] bg-[#050505] p-3 cursor-pointer">
            <input type="checkbox" checked={sendImmediately} onChange={(e) => setSendImmediately(e.target.checked)}
              className="accent-[#FF2D2D] mt-0.5" data-testid="invoice-send-immediately" />
            <div className="flex-1">
              <div className="flex items-center gap-1.5 text-sm">
                <PaperPlaneTilt size={13} weight="fill" className="text-[#FF2D2D]" />
                Send invoice email immediately
              </div>
              <div className="text-[10px] text-[#a3a3a3] mt-0.5">
                Emails the PDF + pay link. Leave unchecked to save as draft.
              </div>
            </div>
          </label>
          )}

          <div className="flex justify-end gap-3 pt-3 border-t border-[#262626]">
            <button type="button" onClick={onClose} className="btn-outline px-4 py-2 text-xs uppercase tracking-wider"
              data-testid="invoice-cancel">Cancel</button>
            <button type="submit" disabled={saving} data-testid="invoice-submit"
              className="btn-primary px-5 py-2 text-xs uppercase tracking-wider flex items-center gap-2 disabled:opacity-50">
              {saving
                ? (isEdit ? "Saving…" : "Creating…")
                : isEdit
                  ? `Save changes · ${formatUSD(total)}`
                  : sendImmediately ? `Create + send · ${formatUSD(total)}` : `Save draft · ${formatUSD(total)}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testid, placeholder, min, max, step }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        min={min} max={max} step={step} data-testid={testid}
        className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-white focus:border-[#FF2D2D] outline-none" />
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className={`flex items-center justify-between px-4 py-2 ${accent ? "border-t border-[#262626]" : ""}`}>
      <div className={`text-[10px] uppercase tracking-[0.3em] ${accent ? "text-[#FF2D2D]" : "text-[#a3a3a3]"}`}>{label}</div>
      <div className={`font-display tabular-nums ${accent ? "text-2xl text-white" : "text-sm text-[#a3a3a3]"}`}>{value}</div>
    </div>
  );
}

function round2(n) { return Math.round(Number(n || 0) * 100) / 100; }

function plus14() {
  const d = new Date();
  d.setDate(d.getDate() + 14);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
